import { useEffect, useRef, useState } from "react";
import { Mic } from "lucide-react";
import { toast } from "sonner";

const MAX = 300;
const TYPES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

// Hold to record, release to send, slide off the button to cancel. Max 5 minutes.
export function VoiceRecorder({ onRecorded, disabled }) {
    const [elapsed, setElapsed] = useState(null);
    const mr = useRef(null);
    const stream = useRef(null);
    const chunks = useRef([]);
    const held = useRef(false);
    const cancelled = useRef(false);
    const startedAt = useRef(0);
    const tick = useRef(null);

    const stop = () => { if (mr.current?.state === "recording") mr.current.stop(); };
    const abort = () => { cancelled.current = true; stop(); };
    useEffect(() => () => abort(), []); // eslint-disable-line react-hooks/exhaustive-deps

    const start = async (e) => {
        e.preventDefault();
        if (disabled || mr.current) return;
        e.currentTarget.releasePointerCapture?.(e.pointerId);
        held.current = true;
        try {
            stream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch {
            held.current = false;
            return toast.error("Allow microphone access to record voice notes");
        }
        if (!held.current) {
            stream.current.getTracks().forEach((t) => t.stop());
            return toast("Hold the mic button while you speak");
        }
        const type = TYPES.find((t) => window.MediaRecorder?.isTypeSupported?.(t));
        const r = new MediaRecorder(stream.current, type ? { mimeType: type } : undefined);
        chunks.current = [];
        cancelled.current = false;
        r.ondataavailable = (ev) => ev.data.size && chunks.current.push(ev.data);
        r.onstop = () => {
            stream.current?.getTracks().forEach((t) => t.stop());
            clearInterval(tick.current);
            const secs = Math.min(MAX, Math.round((Date.now() - startedAt.current) / 1000));
            const blob = new Blob(chunks.current, { type: (r.mimeType || "audio/webm").split(";")[0] });
            mr.current = null;
            setElapsed(null);
            if (cancelled.current) return toast("Voice note cancelled");
            if (secs < 1) return toast("Hold a little longer to record");
            onRecorded(blob, secs);
        };
        r.start(250);
        mr.current = r;
        startedAt.current = Date.now();
        setElapsed(0);
        tick.current = setInterval(() => {
            const s = Math.floor((Date.now() - startedAt.current) / 1000);
            setElapsed(s);
            if (s >= MAX) stop();
        }, 250);
    };
    const release = () => { held.current = false; stop(); };
    const leave = () => { if (!held.current) return; held.current = false; if (mr.current) abort(); };
    const move = (e) => {
        if (!held.current) return;
        const r = e.currentTarget.getBoundingClientRect();
        if (e.clientX < r.left - 30 || e.clientX > r.right + 30 || e.clientY < r.top - 30 || e.clientY > r.bottom + 30) leave();
    };

    return (
        <div className="relative flex items-center">
            {elapsed !== null && (
                <div className="absolute right-12 bottom-0 whitespace-nowrap rounded-full bg-red-50 text-red-700 border border-red-200 px-3 py-2 text-xs font-medium flex items-center gap-2" data-testid="voice-recording-indicator">
                    <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
                    {fmt(elapsed)} / 5:00<span className="hidden sm:inline"> · release to send · slide away to cancel</span>
                </div>
            )}
            <button type="button" disabled={disabled} onPointerDown={start} onPointerUp={release} onPointerMove={move} onPointerLeave={leave} onPointerCancel={leave}
                onContextMenu={(e) => e.preventDefault()} title="Hold to record a voice note"
                className={`p-2.5 rounded-full select-none touch-none transition-transform ${elapsed !== null ? "bg-red-600 text-white scale-125" : "bg-klein text-white hover:bg-klein/90"} disabled:opacity-40`}
                data-testid="chat-voice-record-btn">
                <Mic className="w-5 h-5" />
            </button>
        </div>
    );
}

export default VoiceRecorder;
